/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";
import React, { useEffect, useState } from "react";
import { useModalStore } from "@/store/useModalStore";
import AppButton from "../buttons/AppButton";
import { createPortal } from "react-dom";
import { usePathname, useRouter } from "next/navigation";
import LoadingDots from "../other/LoadingDots";
import AppText from "../other/AppText";
import { useLocale } from "@/store/clientStore";
import { X } from "lucide-react";
import { ecommStore } from "@/store/ecommStore";

interface PopupParams {
  account_number: string | null;
}

export const GlobalCustomerModal = () => {
  const {
    showCustomerModal,
    setShowCustomerModal,
    eguestType,
    setEGuestType,
    accountNumber,
    setAccountNumber,
    error,
    setError,
    reset,
    isLoading,
    setIsLoading,
    setUserExists,
  } = useModalStore();

  const { resetEcommStore } = ecommStore();

  const router = useRouter();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [headings, setHeading] = useState<any>({});
  const { locale } = useLocale();
  const currentPath = usePathname();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  const fetchData = async () => {
    const res = await fetchContentful(locale);
    setHeading(res?.items?.[0]);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let ecommLoader: any;
  if (typeof document !== "undefined") {
    ecommLoader = document.getElementById("ecommLoader");
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let globalLoader: any;
  if (typeof document !== "undefined") {
    globalLoader = document.getElementById("globalLoader");
  }

  if (!showCustomerModal) return null;

  const validateSfccAccount = () => {
    if (!accountNumber) {
      setError(headings?.fields?.invalidErrorMessage);
      setIsLoading(false);
      return false;
    }

    const isValid = /^[0-9]{7,10}$/.test(accountNumber);
    if (!isValid) {
      setError(headings?.fields?.invalidErrorMessage);
      setIsLoading(false);
      return false;
    }

    setError("");
    return true;
  };

  const handleNavigateToAccount = async () => {
    const params: PopupParams = {
      account_number: accountNumber,
    };
    const headers = {
      ecommAccessKey: `${process.env.NEXT_PUBLIC_API_KEY_ECOMM}`,
    };

    try {
      const accountResponseFromAPI: any = await fetch(`/api/validate-account-number`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify(params),
      });
      const accountResponse = await accountResponseFromAPI.json();
      if (accountResponse?.AccountExist === "False") {
        setUserExists(false);
        setAccountNumber("");
        localStorage.setItem("AccountNumber", "");
        setError(headings?.fields?.invalidErrorMessage);
        const res = await fetchContentful(locale);
        setHeading(res?.items?.[0]);
        setIsLoading(false);
        return false;
      } else {
        setUserExists(true);
        setAccountNumber(accountNumber);
        localStorage.setItem("AccountNumber", accountNumber);
        setShowCustomerModal(false);
        setTimeout(() => {
          setIsLoading(false);
          reset();
          if (currentPath === `/${locale}/products/storage-containers`) {
            window.location.reload();
          } else {
            router.push(`/${locale}/products/storage-containers`);
          }
        }, 100);
      }
    } catch (err) {
      setIsLoading(false);
      setError(headings?.fields?.backendErrorMessage);
    }
  };

  const handleContinue = async () => {
    setIsLoading(true);
    resetEcommStore();
    if (eguestType === "no") {
      setIsLoading(false);
      localStorage.setItem("AccountNumber", "");
      reset();

      if (currentPath === `/${locale}/products/storage-containers`) {
        window.location.reload();
      } else {
        router.push(`/${locale}/products/storage-containers`);
      }
    } else if (validateSfccAccount()) {
      handleNavigateToAccount();
    }
  };

  return (
    <>
      {showCustomerModal &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1000,
              pointerEvents: showCustomerModal ? "all" : "none",
              display: showCustomerModal ? "block" : "none",
            }}
          >
            <div className="flex items-center justify-center w-full h-full">
              <div className="w-[733px]">
                <div className="relative flex flex-col justify-center gap-4 p-8 bg-white shadow-lg">
                  <div className="flex justify-end">
                    <button onClick={reset} className="hover:text-black text-lg text-gray-500">
                      <X />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <AppText type="HEADLINE_MEDIUM">{headings?.fields?.heading}</AppText>
                    {/* <button onClick={reset} className="hover:text-black text-lg text-gray-500">
                      <AppText type={"BODY_MEDIUM"}>X</AppText>
                    </button> */}
                  </div>
                  <div
                    role="radiogroup"
                    key="1"
                    className="flex flex-row items-center py-[2px] space-x-3 space-y-0 gap-8"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <input
                        id="1"
                        autoComplete="nope"
                        type="radio"
                        checked={eguestType === "yes"}
                        name="customerType"
                        value="yes"
                        onChange={() => setEGuestType("yes")}
                        className="min-w-4 focus-visible:ring-blue-500/50 ring-2 ring-transparent focus-visible:outline-none w-4 h-4 outline-none"
                      />

                      <AppText type="BODY_MEDIUM_EMPHASIS">{headings?.fields?.secondaryToggleLabel}</AppText>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <input
                        id="2"
                        autoComplete="nope"
                        type="radio"
                        checked={eguestType === "no"}
                        name="customerType"
                        value="no"
                        onChange={() => setEGuestType("no")}
                        className="min-w-4 focus-visible:ring-blue-500/50 ring-2 ring-transparent focus-visible:outline-none w-4 h-4 outline-none"
                      />

                      <AppText type="BODY_MEDIUM_EMPHASIS">{headings?.fields?.primaryToggleLabel}</AppText>
                    </div>
                  </div>
                  {eguestType === "yes" && (
                    <div className="mb-4 w-[50%]">
                      <AppText type="BODY_SMALL" className={`${error ? "text-red-500" : "text-black"}`}>
                        {headings?.fields?.textFieldLabel}
                      </AppText>
                      <input
                        type="number"
                        onKeyDown={(e) => {
                          if (e.key === ".") {
                            e.preventDefault();
                          }
                          if (e.key === "-") {
                            e.preventDefault();
                          }
                          if (e.key === "e" || e.key === "E") {
                            e.preventDefault();
                          }
                          if (e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const digitsOnly = raw.replace(/\D/g, "");

                          if (e.target.value.length <= 10) {
                            setAccountNumber(digitsOnly);
                          }

                          if (e.target.value.length > headings?.fields?.max) {
                            setError(headings?.fields?.maxErrorMessage);
                          } else if (e.target.value.length < headings?.fields?.min) {
                            setError(headings?.fields?.minErrorMessage);
                          } else {
                            setError("");
                          }
                        }}
                        placeholder={headings?.fields?.placeholder}
                        value={accountNumber}
                        className={`flex h-10 w-full outline-none rounded-md border ${
                          error ? "border-red-500" : "border-black"
                        }  bg-white px-3 py-2 text-sm ring-offset-white
                                file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500
                                focus-visible:outline-none focus-visible:ring-2 focus-visible:dijon  
                                focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                             `}
                      />
                      {error.length > 0 && <p className="mt-1 text-sm text-red-500">{error}</p>}
                    </div>
                  )}
                  <div className="flex flex-row flex-wrap justify-end gap-4 bg-white">
                    <AppButton
                      intent="primary"
                      label={
                        eguestType === "yes"
                          ? `${headings?.fields?.secondaryCtaLabel}`
                          : `${headings?.fields?.primaryCtaLabel}`
                      }
                      type="submit"
                      className="px-10"
                      onClick={handleContinue}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>,
          ecommLoader
        )}

      {isLoading &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 1001, // Ensure it's on top
              pointerEvents: isLoading ? "all" : "none",
              display: isLoading ? "block" : "none",
            }}
          >
            <div className="flex items-center justify-center w-full h-full">
              <LoadingDots />
            </div>
          </div>,
          globalLoader
        )}
    </>
  );
};

const fetchContentful = async (lang: string | undefined) => {
  const payload = {
    content_type: "websiteBanner",
    include: 10,
    locale: lang === "en" ? "en-US" : "fr-CA",
    "fields.internalLabel": "Form - Rent a Storage Container - Popup",
  };

  try {
    const data = await fetch("/api/contentful", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const res = await data.json();
    return res;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.log(error);

    return { cardsList: [], total: 0, error: error.message };
  }
};
